import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Flame, Plus, Loader2 } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { Header } from "@/components/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Remembrance() {
  const { t, fontClass } = useLang();
  const [items, setItems] = useState([]);
  const [today, setToday] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", date_of_passing: "", note: "" });
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    axios.get(`${API}/memorials`).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
    axios.get(`${API}/memorials?today=true`).then((r) => setToday(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t("rem_name")); return; }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (photo) fd.append("photo", photo);
    setSaving(true);
    try {
      await axios.post(`${API}/memorials`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("rem_save"));
      setForm({ name: "", date_of_passing: "", note: "" }); setPhoto(null); setShowForm(false); load();
    } catch (err) { toast.error("Could not save."); }
    finally { setSaving(false); }
  };

  const Card = ({ m, highlight }) => (
    <Link to={`/remembrance/${m.id}`} data-testid={`memorial-card-${m.id}`} className="flex items-center gap-4 rounded-3xl p-4 transition-transform hover:scale-[1.01]" style={{ background: highlight ? "linear-gradient(135deg, #1C140E, #3B2A16)" : "var(--card)", color: highlight ? "#FFFDF7" : "var(--text)", border: `2px solid ${highlight ? "var(--accent-glow)" : "var(--border)"}` }}>
      {m.photo_url ? (
        <img src={m.photo_url} alt="" className="w-16 h-16 rounded-2xl object-cover shrink-0" style={{ filter: "grayscale(0.2)" }} />
      ) : (
        <span className="grid place-items-center w-16 h-16 rounded-2xl shrink-0" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><Flame size={26} /></span>
      )}
      <div className="min-w-0 flex-1">
        <p className={`${fontClass()} text-lg font-bold truncate`}>{m.name}</p>
        {m.date_of_passing && <p className="font-ui text-xs" style={{ color: highlight ? "rgba(255,255,255,0.7)" : "var(--text-muted)" }}>{m.date_of_passing}</p>}
        <p className="font-ui text-xs mt-0.5" style={{ color: highlight ? "var(--player-accent)" : "var(--accent)" }}>🪔 {m.sadhu_count} {t("rem_sadhu_count")}</p>
      </div>
    </Link>
  );

  return (
    <div className="min-h-screen pb-24" style={{ background: "var(--bg)" }}>
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6">
        <Link to="/rooms/sindu-kamare" data-testid="rem-back-link" className="inline-flex items-center gap-2 font-ui font-bold text-sm" style={{ color: "var(--accent)" }}>
          <ArrowLeft size={18} /> {t("back_room")}
        </Link>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6">
        <h1 className={`${fontClass()} text-2xl sm:text-3xl font-bold flex items-center gap-2`} style={{ color: "var(--text)" }}>
          <Flame size={26} style={{ color: "var(--accent)" }} /> {t("rem_title")}
        </h1>
        <p className="font-ui text-base mt-2" style={{ color: "var(--text-2)" }}>{t("rem_sub")}</p>

        {today.length > 0 && (
          <div className="mt-6">
            <p className="font-ui text-sm font-bold uppercase tracking-widest mb-2" style={{ color: "var(--accent)" }}>{t("rem_today")}</p>
            <div className="space-y-3">{today.map((m) => <Card key={m.id} m={m} highlight />)}</div>
          </div>
        )}

        <button onClick={() => setShowForm((s) => !s)} data-testid="rem-create-toggle" className="mt-6 w-full h-12 rounded-xl font-ui font-bold text-base flex items-center justify-center gap-2" style={{ background: "var(--accent)", color: "#fff" }}>
          <Plus size={18} /> {t("rem_create")}
        </button>

        {showForm && (
          <form onSubmit={create} className="mt-4 rounded-3xl p-5 space-y-3" style={{ background: "var(--card)", border: "2px solid var(--border)" }} data-testid="rem-form">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("rem_name")} data-testid="rem-name" className="w-full h-12 px-4 rounded-xl font-ui outline-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />
            <label className="block"><span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("rem_date")}</span>
              <input type="date" value={form.date_of_passing} onChange={(e) => setForm({ ...form, date_of_passing: e.target.value })} data-testid="rem-date" className="mt-1 w-full h-12 px-4 rounded-xl font-ui outline-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />
            </label>
            <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t("rem_note")} rows={2} data-testid="rem-note" className="w-full px-4 py-2 rounded-xl font-ui outline-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />
            <label className="block"><span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("rem_photo")}</span>
              <input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files[0])} data-testid="rem-photo" className="mt-1 w-full font-ui text-sm" style={{ color: "var(--text-2)" }} />
            </label>
            <button type="submit" disabled={saving} data-testid="rem-save" className="w-full h-12 rounded-xl font-ui font-bold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--player-bg)", color: "var(--player-accent)" }}>
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Flame size={18} />} {t("rem_save")}
            </button>
          </form>
        )}

        <div className="mt-8 space-y-3">
          {loading ? (
            <p className="text-center font-ui py-6" style={{ color: "var(--text-muted)" }}>{t("loading")}</p>
          ) : items.length === 0 ? (
            <p className="text-center font-ui py-6" style={{ color: "var(--text-muted)" }}>{t("rem_empty")}</p>
          ) : (
            items.map((m) => <Card key={m.id} m={m} />)
          )}
        </div>
      </div>
    </div>
  );
}
